import {
  Button,
  SentFormsView,
  useToast,
  type FmSentFormRow,
  type FmSentFilter,
  type PfPatientFormRecord,
} from '@elva/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { extractApiErrorMessage } from '@/api/errors';
import { PatientFormDetailDrawer } from '@/features/patients/components/PatientFormDetailDrawer';
import { adaptFormAssignments } from '../adapters/formsAdapters';
import { useFormAssignments } from '../hooks/useFormAssignments';
import { deliverFormReminder } from '../deliverFormReminder';
import { useResendAssignment, useResendSubmission } from '../hooks/useFormSubmission';

function toFormRecord(row: FmSentFormRow): PfPatientFormRecord {
  return {
    id: row.id,
    name: row.formName,
    desc: row.formDesc,
    assignmentStatus: row.assignmentStatus,
    syncStatus: row.syncStatus,
    timeline: row.timeline,
    assignmentId: row.id,
    ...(row.channel != null ? { channel: row.channel } : {}),
    ...(row.reviewedBy != null ? { reviewedBy: row.reviewedBy } : {}),
    ...(row.submissionId != null ? { submissionId: row.submissionId } : {}),
  };
}

export function SentFormsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data, isLoading } = useFormAssignments();
  const rows = useMemo(() => adaptFormAssignments(data), [data]);
  const [filter, setFilter] = useState<FmSentFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<FmSentFormRow | null>(null);
  const resendSubmission = useResendSubmission();
  const resendAssignment = useResendAssignment();

  usePageTopBar({
    actions: (
      <Button
        variant="secondary"
        size="sm"
        iconLeft="folder"
        onClick={() => {
          void navigate('/forms/templates');
        }}
      >
        Form templates
      </Button>
    ),
  });

  const handleSendReminder = async (row: FmSentFormRow) => {
    try {
      await deliverFormReminder(
        {
          ...(row.submissionId ? { submissionId: row.submissionId } : {}),
          assignmentId: row.id,
        },
        {
          resendSubmission: (submissionId, channel) =>
            resendSubmission.mutateAsync({ submissionId, body: { channel } }),
          resendAssignment: (assignmentId, channel) =>
            resendAssignment.mutateAsync({ assignmentId, body: { channel } }),
        },
      );
      toast({ title: 'Sent by text + email', variant: 'success' });
    } catch (error) {
      toast({ title: 'Resend failed', description: extractApiErrorMessage(error), variant: 'error' });
    }
  };

  return (
    <div className="fm-root v6-root flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="relative z-[1] flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-[30px] pb-[22px] pt-4">
        <SentFormsView
          rows={rows}
          isLoading={isLoading}
          filter={filter}
          onFilterChange={setFilter}
          search={search}
          onSearchChange={setSearch}
          onOpenPatient={(row) => {
            void navigate(`/patients/${row.patientId}`);
          }}
          onPreview={setSelectedRow}
          onSendReminder={(row) => {
            void handleSendReminder(row);
          }}
        />
      </div>
      <PatientFormDetailDrawer
        form={selectedRow ? toFormRecord(selectedRow) : null}
        patientId={selectedRow ? Number(selectedRow.patientId) : null}
        onClose={() => {
          setSelectedRow(null);
        }}
      />
    </div>
  );
}
